import { useCallback, useEffect, useState } from 'react';
import { getDepositoClientes } from '@spark/services/deposito';
import { notification } from '@spark/ui-components/utils/notification';
import { toApiDate } from '@spark/utils/formatters';

const BOOLEAN_FILTER_KEYS = new Set(['TokenRastreamentoAtivo']);

const parseBooleanFilter = (value) => {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return value;
};

const normalizeFilterValue = (key, value) => {
  if (value instanceof Date) return toApiDate(value);
  if (BOOLEAN_FILTER_KEYS.has(key)) return parseBooleanFilter(value);
  return value;
};

const normalizeFilters = (filtros) =>
  Object.fromEntries(
    Object.entries(filtros)
      .filter(([, value]) => value !== undefined && value !== null && value !== '')
      .map(([key, value]) => [key, normalizeFilterValue(key, value)])
  );

export const useDepositoClientes = (show) => {
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtros, setFiltros] = useState({});
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [quantidadePorPagina, setQuantidadePorPagina] = useState(10);
  const [totalRegistros, setTotalRegistros] = useState(0);

  const carregarClientes = useCallback(async () => {
    if (!show) return;

    try {
      setLoading(true);
      const params = {
        PaginaAtual: paginaAtual,
        QuantidadePorPagina: quantidadePorPagina,
        ...normalizeFilters(filtros)
      };

      const response = await getDepositoClientes(params);
      const data = response?.data || response || {};
      const registros = Array.isArray(data.registros) ? data.registros : [];

      setClientes(registros);
      setTotalRegistros(data.totalRegistros || registros.length);
    } catch (error) {
      console.error('Erro ao carregar clientes do deposito:', error);
      notification.error(
        error?.message || 'Nao foi possivel carregar os clientes do deposito',
        'Erro ao carregar clientes'
      );
      setClientes([]);
      setTotalRegistros(0);
    } finally {
      setLoading(false);
    }
  }, [show, filtros, paginaAtual, quantidadePorPagina]);

  useEffect(() => {
    if (show) {
      carregarClientes();
    }
  }, [show, carregarClientes]);

  const handleFiltrosChange = useCallback((novosFiltros) => {
    setFiltros(novosFiltros);
    setPaginaAtual(1);
  }, []);

  const handlePaginationChange = useCallback(
    (paginationState) => {
      if (paginationState.pageSize !== quantidadePorPagina) {
        setQuantidadePorPagina(paginationState.pageSize);
        setPaginaAtual(1);
      } else if (paginationState.pageIndex !== undefined) {
        setPaginaAtual(paginationState.pageIndex + 1);
      }
    },
    [quantidadePorPagina]
  );

  const resetFiltros = useCallback(() => {
    setFiltros({});
    setPaginaAtual(1);
    setQuantidadePorPagina(10);
  }, []);

  const reload = useCallback(() => {
    carregarClientes();
  }, [carregarClientes]);

  return {
    clientes,
    loading,
    filtros,
    paginaAtual,
    quantidadePorPagina,
    totalRegistros,
    handleFiltrosChange,
    handlePaginationChange,
    resetFiltros,
    reload
  };
};
